import Form from "rc-field-form";
import { useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../../components/Button";
import Field from "../../../../../components/Field";
import Select from "../../../../../components/Inputs/Select";
import Text from "../../../../../components/Inputs/Text";
import { usePeopleApi } from "../../../../../utils/api/people";
import { useProfileApi } from "../../../../../utils/api/profile";
import { generateErrorsConfigForForm } from "../../../../../utils/helpers/errors";
import { renderPeopleCustomFieldLabel } from "../../../../../utils/helpers/settings";
import { useCustomFields } from "../../../../Settings/CustomFields/context";
import { ProfileContext } from "../../../context";
import { useIsFieldDisabled } from "../../../useIsFieldDisabled";

function ChangeCustomFields({ close }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const { peopleCustomFields } = useCustomFields();
    const getIsFieldDisabled = useIsFieldDisabled("peopleApiConnection.customFields");

    const input = useCallback((field) => {
        let Component;
        const placeholder = renderPeopleCustomFieldLabel(field);
        const disabled = getIsFieldDisabled(field.code);
        switch (field.type) {
            case "number":
                Component = <Text type="number" min={0} placeholder={placeholder} disabled={disabled} />
                break;
            case "email":
                Component = <Text type="email" placeholder={placeholder} disabled={disabled} />
                break;
            case "select":
                Component = (
                    <Select placeholder={placeholder} allowClear disabled={disabled}>
                        {field.options.map((option) => (
                            <Select.Option value={option.value} key={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                );
                break;
            case "text":
            default:
                Component = <Text placeholder={placeholder} disabled={disabled} />
                break;
        }
        return Component
    }, [getIsFieldDisabled]);

    const rules = useCallback((field) => {
        const values = [
            {
                required: field.isRequired && !getIsFieldDisabled(field.code),
                message: t("required-custom-field-type", {
                    code: renderPeopleCustomFieldLabel(field),
                })
            }
        ];

        if (field.type === "email") {
            values.push({
                type: "email",
                message: t("invalid-email"),
            });
        }

        if (field.type === "number") {
            values.push({
                min: 0,
                message: t("custom-field-bigger-than-0", {
                    code: renderPeopleCustomFieldLabel(field),
                })
            })
        }

        return values;
    }, [t, getIsFieldDisabled]);

    const { user, submitting, submittingError, submit } =
        useContext(ProfileContext);
    const { changeCustomFields: profileSubmit } = useProfileApi();
    const { changeCustomFields: peopleSubmit } = usePeopleApi();

    const onFinish = useCallback(
        (values) => {
            submit(profileSubmit, peopleSubmit, values, close);
        },
        [submit, profileSubmit, peopleSubmit, close],
    );
    useEffect(() => {
        form.setFieldsValue({ customFields: user?.customFields });
    }, [user, form]);

    useEffect(() => {
        const fieldErrors = generateErrorsConfigForForm(
            [],
            submittingError,
        );
        form.setFields(fieldErrors);
    }, [form, submittingError]);

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
        >
            {peopleCustomFields?.length > 0 && (
                peopleCustomFields.map((field) => (
                    <Field
                        label={renderPeopleCustomFieldLabel(field)}
                        name={["customFields", field.code]}
                        rules={rules(field)}
                    >
                        {input(field)}
                    </Field>
                ))
            )}

            <div className="d-flex justify-content-end">
                <Button
                    className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
                    onClick={close}
                    disabled={submitting}
                >
                    {t("cancel")}
                </Button>

                <Button
                    color="primary"
                    type="submit"
                    loading={submitting}
                    className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
                >
                    {t("save")}
                </Button>
            </div>
        </Form>
    );
}

export default ChangeCustomFields;
